import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import confetti from "canvas-confetti";
import { images } from "@/lib/weddingData";
import { ArrowDown } from "lucide-react";

const isVideo = (url = "") => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
const isGif = (url = "") => /\.gif(\?|$)/i.test(url);

const fireConfetti = () => {
  const colors = ["#891613", "#0a0a0a", "#8a8580", "#ffffff", "#ece7dc"];
  const defaults = { origin: { y: 0.6 }, colors, spread: 70, ticks: 200 };
  confetti({ ...defaults, particleCount: 80, angle: 60, origin: { x: 0, y: 0.7 } });
  confetti({ ...defaults, particleCount: 80, angle: 120, origin: { x: 1, y: 0.7 } });
  confetti({ ...defaults, particleCount: 120, angle: 90, origin: { x: 0.5, y: 0.4 }, spread: 100 });
};

const HeroMedia = () => {
  const src = images.heroMedia;
  const crop = images.heroMediaCrop;
  const commonStyle = {
    height: "clamp(140px, 16vw, 260px)",
    width: "auto",
  };

  if (isVideo(src)) {
    return (
      <video
        src={src}
        autoPlay
        loop
        muted
        playsInline
        style={commonStyle}
        className="block object-cover"
      />
    );
  }

  if (isGif(src) || !crop) {
    return (
      <img
        src={src}
        alt="Macarena y Virginia — hero"
        style={commonStyle}
        className="block object-cover"
        loading="eager"
      />
    );
  }

  return (
    <div
      role="img"
      aria-label="Macarena y Virginia — fotomatón"
      style={{
        height: "clamp(140px, 16vw, 260px)",
        aspectRatio: crop.aspectRatio,
        backgroundImage: `url(${src})`,
        backgroundSize: crop.backgroundSize,
        backgroundPosition: crop.backgroundPosition,
        backgroundRepeat: "no-repeat",
      }}
    />
  );
};

const Hero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const mediaY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const namesY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section
      id="top"
      ref={heroRef}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-24 pb-16 md:pt-28 md:pb-20"
    >
      <div className="mx-auto w-full max-w-[1800px] px-6 md:px-10">
        {/* Composition: Macarena [ video ] Virginia */}
        <motion.div
          style={{ y: namesY }}
          className="flex flex-col items-center justify-center gap-2 md:flex-row md:gap-6"
        >
          <motion.h1
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-names"
            onClick={fireConfetti}
            className="hero-script text-center md:text-right cursor-pointer select-none"
            style={{ WebkitTapHighlightColor: "transparent" }}
            title="Click for surprise ✨"
          >
            Macarena
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative shrink-0"
            style={{ y: mediaY }}
          >
            <HeroMedia />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            onClick={fireConfetti}
            className="hero-script text-center md:text-left cursor-pointer select-none"
            style={{ WebkitTapHighlightColor: "transparent" }}
            title="Click for surprise ✨"
          >
            Virginia
          </motion.h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-16 text-center md:mt-20"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#0a0a0a]">
            4 · Junio · 2027
          </p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.24em] text-[#4a4a4a]">
            Cortijo de Enmedio — Granada, España
          </p>
        </motion.div>

        <motion.a
          href="#date"
          data-testid="hero-cta-scroll"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="mt-12 flex justify-center md:mt-16"
          aria-label="Continuar"
        >
          <ArrowDown strokeWidth={1.5} className="h-5 w-5 animate-pulse" />
        </motion.a>
      </div>
    </section>
  );
};

export default Hero;
